import { AfterContentChecked, AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { BlendedPortfolioTransformationService, BlendPortfolioUtil, ViewBlendStructureDialogComponent } from '@ddt-web/blending';
import { APP_STORE_CONFIG_TOKEN, ColorsConfig } from '@ddt-web/config';
import { DashboardConfigKey, InjectConfig, PcConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig, ComponentData, ProductPageLinkResolverService, SecurityDataUtil, TableData } from '@ddt-web/data-service';
import { DialogService } from '@ddt-web/dialog';
import { TooltipPosition } from '@ddt-web/dom';
import { Debounce, Memoize } from '@ddt-web/generic-utils';
import { StoreService } from '@ddt-web/store-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as values from 'lodash/values';
import { Subscription } from 'rxjs';
import { first, take } from 'rxjs/operators';

import { DefaultScrollableHoldingsTableConfig } from '../../config';
import { ExpandedRowDetails, HoldingDescriptionTableHeader, HoldingIdentifierTableHeader, isHoldingDescriptionTableHeader, isHoldingIdentifierTableHeader, isIconFirstLeftTableHeaderConfig, isIconTableHeader, ScrollableHoldingsTableConfig } from '../../interfaces';
import { ScrollSyncService } from '../../services/scroll-sync.service';
import { HoldingsTableUtil } from '../../utils';

@UntilDestroy()
@Component({
	selector: 'hl-scrollable-holdings-table',
	templateUrl: './scrollable-holdings-table.component.html',
	styleUrls: ['./scrollable-holdings-table.component.scss']
})
export class ScrollableHoldingsTableComponent implements AfterViewInit, OnInit, OnChanges, AfterViewChecked, AfterContentChecked, OnDestroy {

	@Input() public data: ComponentData;
	@Input() public config?: ScrollableHoldingsTableConfig;
	@Input() public rowsToHide?: Array<number> = []; // indices to hide when generating categoryIndexMap
	@Input() public excludePortfolioIndices?: Array<number>; // portfolios indices to hide to generating the categoryWeightMap
	@Input() public hideChildrenRows?: boolean = false;

	@Output() public onHoldingsRowExpanded = new EventEmitter<ExpandedRowDetails>();

	@ViewChild('rightTableHeader') public rightTableHeader: ElementRef;
	@ViewChild('leftTableHeader') public leftTableHeader: ElementRef;

	@InjectConfig(DashboardConfigKey.DYNAMIC_HOLDINGS_TABLE) private readonly isDynamicHoldingsTable: boolean = false;
	@InjectConfig(SharedConfigKey.ASSET_CLASS_COLORS) private readonly assetClassColors = ColorsConfig.ASSET_CLASS_COLORS;
	@InjectConfig(PcConfigKey.ENABLE_BLENDING) public readonly enableBlending = false;

	public categoryIndexMap: Map<string, Array<number>>;
	public categoryWeightMap: Map<string, Array<number>>;
	public categoriesArray: Array<string>;
	public productIdPerTickerMap: Map<string, number>;
	public tableData: TableData;
	public colorsConfig = this.assetClassColors;
	public componentBreakpoint: number;
	public numberOfPortfolios: number;
	public scrollingTableDisplayType: string;
	public rightColClass: string = 'col-6';
	public leftColClass: string = 'col-18';
	public decimalPipeDigitsInfo: string = '1.2-2';
	public showTableHeaders: boolean;
	public reserveFirstCol: boolean = false; // if you want reserve the first column with asset classes starting in col 2 or can be used as left col padding in the table
	public assetCategoryColSpan: number = 2;
	public holdingDescriptionIndex: number = 1;
	public HoldingsTableUtil = HoldingsTableUtil;
	public TooltipPosition = TooltipPosition;
	public hideRightTable: boolean = false;
	public showPortfolioStructureInfo: Array<string>;

	// used to track row heights to normalise heights across both left and right tables
	public holdingRowsHeights: Array<Array<number>> = [];

	public idHeader: HoldingIdentifierTableHeader;
	public descriptionHeader: HoldingDescriptionTableHeader;
	public isIconTableHeader = isIconTableHeader;

	private DISPLAY_BLOCK = 'block';
	private DISPLAY_TABLE = 'table';
	private TEXT_ALIGN_RIGHT = 'right';
	private TEXT_ALIGN_CENTER = 'center';
	private readonly PREFIX = {
		PLUS: '+',
		MINUS: '-'
	};
	public readonly CELL_CLASS = {
		MINUS: 'minus-prefix',
		PLUS: 'plus-prefix'
	};
	public selectedExpandedRow = {
		category: null,
		rowIndex: null
	};

	private scrollLeftSubscription: Subscription;

	@ViewChild('scrollableTable', { read: ElementRef }) public scrollableTable: ElementRef<any>;

	constructor(
		private contentService: ContentService,
		private renderer: Renderer2,
		private linkResolverService: ProductPageLinkResolverService,
		private cdr: ChangeDetectorRef,
		private scrollSyncService: ScrollSyncService,
		private dialogService: DialogService,
		private blendedPortfolioTransformationService: BlendedPortfolioTransformationService,
		private storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) { }

	public ngOnInit(): void {
		this.setScrollingTableDisplayType(this.numberOfPortfolios, this.componentBreakpoint);
	}

	public ngAfterViewInit(): void {
		this.resizeHeaders();
	}

	public ngAfterViewChecked(): void {
		this.scrollLeftSubscription = this.scrollSyncService.scrollLeft$
			.pipe(take(1))
			.subscribe(position => {
				if (this.scrollableTable) {
					this.scrollableTable.nativeElement.scrollLeft = position;
				}
			});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes.config && !this.config) {
			this.config = DefaultScrollableHoldingsTableConfig.HOLDINGS_TABLE;
		}

		const componentBreakpointDcr = Number(this.contentService.get('COMPONENT_BREAKPOINT', String(this.config?.options?.componentBreakpoint)));
		this.componentBreakpoint = isNaN(componentBreakpointDcr) ? this.config?.options?.componentBreakpoint : componentBreakpointDcr;

		const tab = this.data.getTabById(this.config?.dataTabId);
		this.hideRightTable = this.isDynamicHoldingsTable && this.data.portfolioData[0].find(cell => cell.config.id === 'subType')?.formattedValue === 'TAX-REPORT';

		this.rightColClass = this.hideRightTable ? 'col-24 dz-col-24' : (this.config.options?.rightColClass ?? this.rightColClass);
		this.leftColClass = this.config.options?.leftColClass ?? this.leftColClass;
		this.decimalPipeDigitsInfo = this.config.options?.decimalPipeDigitsInfo ?? this.decimalPipeDigitsInfo;

		if (this.data.success && !tab?.error) {
			this.tableData = tab?.tableData?.[0];

			if (this.tableData) {
				this.categoryIndexMap = HoldingsTableUtil.populateCategoryIndexMap(this.tableData, this.rowsToHide);
				this.categoryWeightMap = HoldingsTableUtil.populateCategoryWeightMap(this.tableData, this.categoryIndexMap, this.excludePortfolioIndices);
				this.categoriesArray = Array.from(this.categoryIndexMap.keys());
				this.countNumberOfPortfolios();

				if (this.config.options?.showProductPageLinks) {
					this.productIdPerTickerMap = SecurityDataUtil.populateProductIdPerTickerMap(tab);
				}
			}
		}
		this.showTableHeaders = !!this.config?.header?.leftTableHeaderConfigs?.length;
		if (this.showTableHeaders) {
			this.idHeader = this.config.header.leftTableHeaderConfigs.find(isHoldingIdentifierTableHeader);
			this.descriptionHeader = this.config.header.leftTableHeaderConfigs.find(isHoldingDescriptionTableHeader);
			this.reserveFirstCol = isIconFirstLeftTableHeaderConfig(this.config.header.leftTableHeaderConfigs);
			this.assetCategoryColSpan = this.config.header.leftTableHeaderConfigs.length;
		}
		this.holdingDescriptionIndex = this.descriptionHeader?.dataIndex ?? 1;
		this.showViewStructureButton();
	}

	public ngAfterContentChecked(): void {
		this.cdr.detectChanges();
	}

	public ngOnDestroy() {
		this.scrollLeftSubscription.unsubscribe();
	}

	public onScroll(event: Event) {
		const scrollLeftPosition = (event.target as HTMLElement).scrollLeft;
		this.scrollSyncService.sendScrollLeftPosition(scrollLeftPosition);
	}

	public zeroSort(): number {
		return 0;
	}

	private countNumberOfPortfolios(): void {
		this.numberOfPortfolios = 0;
		this.tableData['headersX'].forEach(element => {
			if (element.formattedValue) {
				this.numberOfPortfolios++;
			}
		});
	}

	private setScrollingTableDisplayType(numberOfPortfolios: number, componentBreakpoint: number): void {
		if (numberOfPortfolios > componentBreakpoint) {
			this.scrollingTableDisplayType = this.DISPLAY_BLOCK;
		} else {
			this.scrollingTableDisplayType = this.DISPLAY_TABLE;
		}
	}

	public getCellClass(assetIndex: number, portfolioIndex: number): string {
		const cellValue = this.tableData.cells[assetIndex][this.tableData.cells[assetIndex].length - (this.tableData.portfolioData.length - portfolioIndex)].formattedValue;

		if (cellValue.includes(this.PREFIX.PLUS)) {
			return this.CELL_CLASS.PLUS;
		} else if (+cellValue && cellValue.includes(this.PREFIX.MINUS)) {
			return this.CELL_CLASS.MINUS;
		} else {
			return '';
		}
	}

	public getTextAlignOverride(): string {
		if (this.numberOfPortfolios === 1) {
			return this.TEXT_ALIGN_CENTER;
		} else {
			return this.TEXT_ALIGN_RIGHT;
		}
	}

	@HostListener('window:resize', ['$event'])
	@Debounce()
	public onResize(event): void {
		this.resizeHeaders();
	}

	public onHoldingsRowHeightChange(height: { index: number; elementMeasurements: Array<number> }): void {
		this.holdingRowsHeights[height.index] = height.elementMeasurements;
	}

	public expandRow(holdingCategoryName: string, holdingRowIndex: number): void {
		this.selectedExpandedRow.category = holdingCategoryName;
		this.selectedExpandedRow.rowIndex = holdingRowIndex;

		this.onHoldingsRowExpanded.emit({ holdingCategoryName, holdingRowIndex, holdingRowCells: this.tableData.cells[holdingRowIndex] });
	}

	public closeRow(): void {
		this.selectedExpandedRow.category = null;
		this.selectedExpandedRow.rowIndex = null;

		this.onHoldingsRowExpanded.emit({ holdingCategoryName: null, holdingRowIndex: null, holdingRowCells: null });
	}

	private resizeHeaders(): void {
		if (this.leftTableHeader && this.rightTableHeader) {
			this.renderer.setStyle(this.leftTableHeader.nativeElement, 'height', 'auto');
			this.renderer.setStyle(this.rightTableHeader.nativeElement, 'height', 'auto');
			const leftHeight: number = this.leftTableHeader.nativeElement.offsetHeight;
			const rightHeight: number = this.rightTableHeader.nativeElement.offsetHeight;

			if (leftHeight !== rightHeight) {
				if (leftHeight > rightHeight) {
					this.renderer.setStyle(this.rightTableHeader.nativeElement, 'height', `${leftHeight}px`);
				} else {
					this.renderer.setStyle(this.leftTableHeader.nativeElement, 'height', `${rightHeight}px`);
				}
			}
		}
	}

	@Memoize()
	public populatePageUrl(assetIndex: number): string {
		const portfolioId = this.productIdPerTickerMap.get(this.tableData.headersY[assetIndex]?.formattedValue);
		return this.linkResolverService.getUrlByCellData(this.tableData.cells[assetIndex], 'assetTypeClass', portfolioId?.toString());
	}

	public showViewStructureButton(): void {
		this.storeService.selectAll(this.storeConfig.associatedPortfolios)
			.pipe(first())
			.subscribe(portfolios => {
				const blendedPortfolios = values(portfolios.entities)
					.filter(portfolio => {
						const isContainsChildrenPortfoliosFlag = portfolio?.flags?.containsChildrenPortfolios ?? false;
						const isPortfolioWithChildPortfolios = portfolio?.children?.some(child => BlendPortfolioUtil.isChildPortfolio(child));
						return isPortfolioWithChildPortfolios || isContainsChildrenPortfoliosFlag;
					})
					.map(portfolio => portfolio.id);
				this.showPortfolioStructureInfo = blendedPortfolios;
			});
	}

	public showPortfolioStructurInfoDialog(portfolioId: string): void {
		this.dialogService.showLoading();
		this.blendedPortfolioTransformationService.buildTreeNodeMapFromFlattenedPortfolios(portfolioId, this.storeConfig)
			.pipe(untilDestroyed(this))
			.subscribe(treeNodeMap => {
				this.dialogService.close();
				this.dialogService.open(ViewBlendStructureDialogComponent, { showClose: true, data: { treeNodeMap, portfolioId } });
			});
	}
}
